import * as THREE from 'three/webgpu';
import {
  Fn,
  float,
  vec2,
  vec3,
  sin,
  fract,
  dot,
  abs,
  max,
  floor,
  mod,
  smoothstep,
  time,
  positionLocal,
  normalLocal,
} from 'three/tsl';

/**
 * Escape-hatch type for TSL node expressions whose exact generic is not
 * surfaced by `@types/three`. Replaces per-file `type TslNode = any`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TslNode = any;

/**
 * Classic GPU hash: `fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453)`.
 * Shared across cinematic post, arena force-field, pickup shimmer, and VFX
 * glitch shaders.
 */
export const hash21 = Fn(([p]: [ReturnType<typeof vec2>]) => {
  return fract(sin(dot(p, vec2(12.9898, 78.233))).mul(43758.5453));
});

/**
 * Single-float variant: `fract(sin(n) * 43758.5453)`.
 */
export const hash11 = Fn(([n]: [ReturnType<typeof float>]) => {
  return fract(sin(n).mul(43758.5453));
});

/**
 * Hash with different seed constants (127.1, 311.7) used for spatial noise.
 */
export const hash21b = Fn(([p]: [ReturnType<typeof vec2>]) => {
  return fract(sin(dot(p, vec2(127.1, 311.7))).mul(43758.5453));
});

/**
 * Assign `emissiveNode` on any `NodeMaterial` subclass.
 *
 * The runtime `NodeMaterial.setupLighting()` path reads `emissiveNode` on
 * every subclass (MeshBasicNodeMaterial, PointsNodeMaterial, etc.), but the
 * public `@types/three` declarations only expose the property on
 * `MeshStandardNodeMaterial`. This helper hides the single unsafe cast.
 */
export function setEmissiveNode(material: THREE.Material, node: unknown): void {
  (material as unknown as { emissiveNode: unknown }).emissiveNode = node;
}

/**
 * Hex cell distance metric used by the arena force-field and BR safe-zone
 * shaders. Given a 2D offset from a hex cell's center, returns the distance
 * to the nearest hex-edge plane (flat-top metric).
 */
export const hexDist = Fn(([p]: [ReturnType<typeof vec2>]) => {
  const q = abs(p).toVar();
  return max(q.x.mul(0.866025).add(q.y.mul(0.5)), q.y);
});

/**
 * Staggered hex-grid lattice in UV space. Returns ~1 inside hex cells and
 * fades to 0 on their edges, producing a honeycomb line pattern when
 * combined with the usual shader color mix.
 */
export const hexGrid = Fn(
  ([uvIn, scale]: [ReturnType<typeof vec2>, ReturnType<typeof float>]) => {
    const g = uvIn.mul(scale);
    const id = floor(g);
    const f = fract(g).sub(0.5).toVar();
    f.x = f.x.sub(
      mod(id.y, 2.0).greaterThan(0.5).select(float(0.5), float(0.0)),
    );
    const d = hexDist(f);
    return smoothstep(0.46, 0.5, d);
  },
);

/**
 * Subtle sine displacement along the vertex normal, animated in time.
 * Drives the force-field "breathing" look on arena walls and BR zone.
 */
export const forceFieldPosition = Fn(() => {
  const displacement = sin(
    time.mul(2.2).add(dot(positionLocal, vec3(1.0, 1.0, 1.0))),
  ).mul(0.08);
  return positionLocal.add(normalLocal.mul(displacement));
});
